if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        this.aaVariantChangeUnsubscriber = null;

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener(
            'slideChanged',
            debounce(this.onSlideChanged.bind(this), 500)
          );
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails
            .querySelectorAll('[data-target]')
            .forEach((mediaToSwitch) => {
              const button = mediaToSwitch.querySelector('button');

              if (!button) return;

              button.addEventListener(
                'click',
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false
                )
              );
            });

          if (
            this.dataset.desktopLayout.includes('thumbnail') &&
            this.mql.matches
          ) {
            this.removeListSemantic();
          }
        }

        this.aaSetupColorGallery();
      }

      disconnectedCallback() {
        if (this.aaVariantChangeUnsubscriber) {
          this.aaVariantChangeUnsubscriber();
          this.aaVariantChangeUnsubscriber = null;
        }
      }

      /* ================================================
         APPAREL ALCHEMISTS COLOUR GALLERY
         ================================================ */

      aaNormalize(value) {
        return (value || '')
          .toString()
          .trim()
          .toLowerCase()
          .replace(/\s+/g, ' ');
      }

      aaGetSectionId() {
        const galleryId = this.id || '';

        if (galleryId.startsWith('MediaGallery-')) {
          return galleryId.replace('MediaGallery-', '');
        }

        return '';
      }

      aaGetVariantPicker() {
        const sectionId = this.aaGetSectionId();

        if (sectionId) {
          const exactPicker = document.getElementById(
            `variant-selects-${sectionId}`
          );

          if (exactPicker) return exactPicker;
        }

        return (
          this.closest('.shopify-section')
            ?.querySelector('variant-selects') ||
          document.querySelector('variant-selects')
        );
      }

      aaGetSelectedColor() {
        const picker = this.aaGetVariantPicker();

        if (!picker) return '';

        const fieldsets = picker.querySelectorAll('fieldset');

        for (const fieldset of fieldsets) {
          const legend = fieldset.querySelector('legend');

          const optionName = this.aaNormalize(
            legend?.textContent || ''
          );

          if (
            optionName.includes('color') ||
            optionName.includes('colour')
          ) {
            const checked = fieldset.querySelector(
              'input[type="radio"]:checked'
            );

            if (checked?.value) {
              return checked.value;
            }

            const selectedValue = fieldset.querySelector(
              '[data-selected-value]'
            );

            if (selectedValue?.textContent?.trim()) {
              return selectedValue.textContent.trim();
            }
          }
        }

        const dropdowns = picker.querySelectorAll(
          '.product-form__input--dropdown'
        );

        for (const wrapper of dropdowns) {
          const label = wrapper.querySelector('label');

          const optionName = this.aaNormalize(
            label?.textContent || ''
          );

          if (
            optionName.includes('color') ||
            optionName.includes('colour')
          ) {
            const select = wrapper.querySelector('select');

            if (select?.value) {
              return select.value;
            }
          }
        }

        return '';
      }

      aaSetupColorGallery() {
        if (
          typeof subscribe === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS.variantChange
        ) {
          this.aaVariantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            ({ data }) => {
              const sectionId = this.aaGetSectionId();

              if (
                data?.sectionId &&
                sectionId &&
                String(data.sectionId) !== String(sectionId)
              ) {
                return;
              }

              window.requestAnimationFrame(() => {
                window.requestAnimationFrame(() => {
                  this.aaApplyColorGallery();
                });
              });
            }
          );
        }

        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            this.aaApplyColorGallery();
          });
        });
      }

      aaApplyColorGallery() {
        if (!this.elements.viewer) return;

        const mediaItems = Array.from(
          this.elements.viewer.querySelectorAll(
            '.product__media-item[data-media-id]'
          )
        );

        if (!mediaItems.length) return;

        const selectedColor = this.aaNormalize(
          this.aaGetSelectedColor()
        );

        if (!selectedColor) return;

        const matchingTaggedMedia = mediaItems.filter(
          (item) =>
            this.aaNormalize(item.dataset.aaColor) ===
            selectedColor
        );

        const hasTaggedGroup =
          matchingTaggedMedia.length > 0;

        if (!hasTaggedGroup) return;

        const visibleMediaIds = new Set();

        mediaItems.forEach((item) => {
          const itemColor = this.aaNormalize(
            item.dataset.aaColor
          );

          const isCommon =
            item.dataset.aaCommon === 'true';

          let visible =
            isCommon ||
            itemColor === selectedColor;

          if (visible) {
            if (visibleMediaIds.has(item.dataset.mediaId)) {
              visible = false;
            } else {
              visibleMediaIds.add(item.dataset.mediaId);
            }
          }

          item.hidden = !visible;
          item.style.display = visible ? '' : 'none';

          if (this.elements.thumbnails) {
            this.elements.thumbnails
              .querySelectorAll(
                `[data-target="${item.dataset.mediaId}"]`
              )
              .forEach((thumbnail) => {
                thumbnail.hidden = !visible;
                thumbnail.style.display =
                  visible ? '' : 'none';
              });
          }
        });

        const visibleItems = mediaItems.filter(
          (item) =>
            !item.hidden &&
            item.style.display !== 'none'
        );

        if (!visibleItems.length) return;

        const preferredItem =
          visibleItems.find(
            (item) =>
              this.aaNormalize(item.dataset.aaColor) ===
              selectedColor
          ) ||
          visibleItems.find(
            (item) =>
              item.dataset.aaCommon === 'true'
          ) ||
          visibleItems[0];

        mediaItems.forEach((item) => {
          item.classList.remove('is-active');
        });

        preferredItem.classList.add('is-active');

        if (this.elements.thumbnails) {
          this.elements.thumbnails
            .querySelectorAll('button')
            .forEach((button) => {
              button.removeAttribute('aria-current');
            });

          const activeThumbnail =
            this.elements.thumbnails.querySelector(
              `[data-target="${preferredItem.dataset.mediaId}"]`
            );

          activeThumbnail
            ?.querySelector('button')
            ?.setAttribute('aria-current', 'true');
        }

        const total = this.querySelector(
          '.slider-counter--total'
        );

        const current = this.querySelector(
          '.slider-counter--current'
        );

        if (total) {
          total.textContent = String(visibleItems.length);
        }

        if (current) {
          current.textContent = '1';
        }

        window.requestAnimationFrame(() => {
          this.elements.viewer?.resetPages?.();
          this.elements.thumbnails?.resetPages?.();

          if (
            preferredItem.parentElement &&
            typeof preferredItem.parentElement.scrollTo ===
              'function'
          ) {
            preferredItem.parentElement.scrollTo({
              left: preferredItem.offsetLeft,
              behavior: 'auto',
            });
          }
        });
      }

      /* ================================================
         STANDARD DAWN MEDIA GALLERY
         ================================================ */

      onSlideChanged(event) {
        const currentElement =
          event?.detail?.currentElement;

        /*
         * AA safety fix:
         * Filtering/recalculating slides can momentarily
         * produce a slideChanged event without a current
         * slide. Dawn must not try to read dataset from
         * an undefined element.
         */
        if (!currentElement?.dataset?.mediaId) {
          return;
        }

        if (!this.elements.thumbnails) {
          return;
        }

        const thumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${currentElement.dataset.mediaId}"]`
          );

        if (!thumbnail) {
          return;
        }

        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(
            `[data-media-id="${mediaId}"]`
          ) ||
          this.elements.viewer.querySelector(
            '[data-media-id]'
          );

        if (!activeMedia) return;

        this.elements.viewer
          .querySelectorAll('[data-media-id]')
          .forEach((element) => {
            element.classList.remove('is-active');
          });

        activeMedia.classList.add('is-active');

        if (prepend) {
          if (
            activeMedia.parentElement.firstChild !==
            activeMedia
          ) {
            activeMedia.parentElement.prepend(
              activeMedia
            );
          }

          if (this.elements.thumbnails) {
            const activeThumbnail =
              this.elements.thumbnails.querySelector(
                `[data-target="${mediaId}"]`
              );

            if (
              activeThumbnail &&
              activeThumbnail.parentElement.firstChild !==
                activeThumbnail
            ) {
              activeThumbnail.parentElement.prepend(
                activeThumbnail
              );
            }
          }

          if (this.elements.viewer.slider) {
            this.elements.viewer.resetPages();
          }
        }

        this.preventStickyHeader();

        window.setTimeout(() => {
          if (
            !this.mql.matches ||
            this.elements.thumbnails
          ) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }

          const activeMediaRect =
            activeMedia.getBoundingClientRect();

          if (activeMediaRect.top > -0.5) return;

          const top =
            activeMediaRect.top +
            window.scrollY;

          window.scrollTo({
            top,
            behavior: 'smooth',
          });
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail =
          this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );

        this.setActiveThumbnail(activeThumbnail);

        if (activeThumbnail) {
          this.announceLiveRegion(
            activeMedia,
            activeThumbnail.dataset.mediaPosition
          );
        }
      }

      setActiveThumbnail(thumbnail) {
        if (
          !this.elements.thumbnails ||
          !thumbnail
        ) {
          return;
        }

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => {
            element.removeAttribute('aria-current');
          });

        thumbnail
          .querySelector('button')
          ?.setAttribute('aria-current', true);

        if (
          this.elements.thumbnails.isSlideVisible?.(
            thumbnail,
            10
          )
        ) {
          return;
        }

        this.elements.thumbnails.slider?.scrollTo({
          left: thumbnail.offsetLeft,
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          '.product__modal-opener--image img'
        );

        if (
          !image ||
          !this.elements.liveRegion
        ) {
          return;
        }

        image.onload = () => {
          this.elements.liveRegion.setAttribute(
            'aria-hidden',
            false
          );

          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              '[index]',
              position
            );

          setTimeout(() => {
            this.elements.liveRegion.setAttribute(
              'aria-hidden',
              true
            );
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        const deferredMedia =
          activeItem.querySelector('.deferred-media');

        if (deferredMedia) {
          deferredMedia.loadContent(false);
        }
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader ||
          document.querySelector('sticky-header');

        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(
          new Event('preventHeaderReveal')
        );
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;

        this.elements.viewer.slider.setAttribute(
          'role',
          'presentation'
        );

        this.elements.viewer.sliderItems.forEach(
          (slide) => {
            slide.setAttribute(
              'role',
              'presentation'
            );
          }
        );
      }
    }
  );
}